import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Loader } from './components/ui';

// Auth
import Login from './pages/auth/Login';

// Admin
import DashboardAdmin from './pages/admin/Dashboard';
import Etablissements from './pages/admin/Etablissements';
import Abonnements from './pages/admin/Abonnements';
import CommandesAdmin from './pages/admin/Commandes';
import ProduitsAdmin from './pages/admin/Produits';
import Stocks from './pages/admin/Stocks';
import KitsAdmin from './pages/admin/Kits';
import VentesAdmin from './pages/admin/Ventes';
import Utilisateurs from './pages/admin/Utilisateurs';
import Rapports from './pages/admin/Rapports';

// Propriétaire
import DashboardProprietaire from './pages/proprietaire/Dashboard';
import EmployesProprietaire from './pages/proprietaire/Employes';
import ProduitsProprietaire from './pages/proprietaire/Produits';
import KitsProprietaire from './pages/proprietaire/Kits';
import VentesProprietaire from './pages/proprietaire/Ventes';
import StocksProprietaire from './pages/proprietaire/Stocks';
import CommandesProprietaire from './pages/proprietaire/Commandes';
import ApprovisionnementProprietaire from './pages/proprietaire/Approvisionnement';
import AbonnementProprietaire from './pages/proprietaire/Abonnement';
import RetourPaiement from './pages/paiement/Retour';
import SimulationPaiement from './pages/paiement/Simulation';

// Gérant
import DashboardGerant from './pages/gerant/Dashboard';
import ProduitsGerant from './pages/gerant/Produits';
import KitsGerant from './pages/gerant/Kits';
import VentesGerant from './pages/gerant/Ventes';

// Serveur / Caissier
import CaisseServeur from './pages/serveur/Caisse';
import CaisseCaissier from './pages/caissier/Caisse';

const RouteProtegee = ({ children, roles }) => {
  const { utilisateur, estConnecte, chargement } = useAuth();
  if (chargement) return <Loader />;
  if (!estConnecte) return <Navigate to="/login" />;
  if (roles && !roles.includes(utilisateur.role)) {
    return <Navigate to="/login" />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Navigate to="/login" />} />

          {/* ── ADMIN ── */}
          <Route path="/admin" element={
            <RouteProtegee roles={['admin']}><DashboardAdmin /></RouteProtegee>
          } />
          <Route path="/admin/etablissements" element={
            <RouteProtegee roles={['admin']}><Etablissements /></RouteProtegee>
          } />
          <Route path="/admin/abonnements" element={
            <RouteProtegee roles={['admin']}><Abonnements /></RouteProtegee>
          } />
          <Route path="/admin/commandes" element={
            <RouteProtegee roles={['admin']}><CommandesAdmin /></RouteProtegee>
          } />
          <Route path="/admin/produits" element={
            <RouteProtegee roles={['admin']}><ProduitsAdmin /></RouteProtegee>
          } />
          <Route path="/admin/stocks" element={
            <RouteProtegee roles={['admin']}><Stocks /></RouteProtegee>
          } />
          <Route path="/admin/kits" element={
            <RouteProtegee roles={['admin']}><KitsAdmin /></RouteProtegee>
          } />
          <Route path="/admin/ventes" element={
            <RouteProtegee roles={['admin']}><VentesAdmin /></RouteProtegee>
          } />
          <Route path="/admin/utilisateurs" element={
            <RouteProtegee roles={['admin', 'proprietaire']}><Utilisateurs /></RouteProtegee>
          } />
          <Route path="/admin/rapports" element={
            <RouteProtegee roles={['admin']}><Rapports /></RouteProtegee>
          } />

          {/* ── PROPRIÉTAIRE ── */}
          <Route path="/proprietaire" element={
            <RouteProtegee roles={['proprietaire']}><DashboardProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/employes" element={
            <RouteProtegee roles={['proprietaire']}><EmployesProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/produits" element={
            <RouteProtegee roles={['proprietaire']}><ProduitsProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/kits" element={
            <RouteProtegee roles={['proprietaire']}><KitsProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/ventes" element={
            <RouteProtegee roles={['proprietaire']}><VentesProprietaire /></RouteProtegee>
          } />

          <Route path="/proprietaire/stocks" element={
            <RouteProtegee roles={['proprietaire']}><StocksProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/commandes" element={
            <RouteProtegee roles={['proprietaire']}><CommandesProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/approvisionnement" element={
            <RouteProtegee roles={['proprietaire']}><ApprovisionnementProprietaire /></RouteProtegee>
          } />
          <Route path="/proprietaire/abonnement" element={
            <RouteProtegee roles={['proprietaire']}><AbonnementProprietaire /></RouteProtegee>
          } />

          {/* ── PAIEMENT EN LIGNE (retour CinetPay, guichet de test) ── */}
          <Route path="/paiement/retour" element={
            <RouteProtegee roles={['proprietaire', 'admin']}><RetourPaiement /></RouteProtegee>
          } />
          <Route path="/paiement/simulation" element={
            <RouteProtegee roles={['proprietaire', 'admin']}><SimulationPaiement /></RouteProtegee>
          } />

          {/* ── GÉRANT ── */}
          <Route path="/gerant" element={
            <RouteProtegee roles={['gerant']}><DashboardGerant /></RouteProtegee>
          } />
          <Route path="/gerant/produits" element={
            <RouteProtegee roles={['gerant']}><ProduitsGerant /></RouteProtegee>
          } />
          <Route path="/gerant/kits" element={
            <RouteProtegee roles={['gerant']}><KitsGerant /></RouteProtegee>
          } />
          <Route path="/gerant/ventes" element={
            <RouteProtegee roles={['gerant']}><VentesGerant /></RouteProtegee>
          } />

          {/* ── SERVEUR / CAISSIER ── */}
          <Route path="/serveur" element={
            <RouteProtegee roles={['serveur']}><CaisseServeur /></RouteProtegee>
          } />
          <Route path="/caissier" element={
            <RouteProtegee roles={['caissier']}><CaisseCaissier /></RouteProtegee>
          } />

        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;